package com.qrkontaq.ui.tenant

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.qrkontaq.ui.map.CARTO_VOYAGER_TILES
import com.qrkontaq.ui.map.ensureLeaflet
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

private const val TENANT_MAP_ID = "qk-tenant-map"

/**
 * Read-only location map for the guest menu site — same clean CARTO style as the admin picker,
 * with a custom marker: the restaurant's logo in a pin above a ground dot.
 */
@Composable
internal fun TenantMap(
    lat: Double?,
    lng: Double?,
    address: String?,
    logoUrl: String?,
    accentHex: String,
) {
    LaunchedEffect(lat, lng, address) {
        ensureLeaflet()
        initTenantMap(lat, lng, address, logoUrl, accentHex)
    }

    Box(
        Modifier
            .fillMaxWidth()
            .height(190.px)
            .borderRadius(14.px)
            .margin(top = 4.px)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .backgroundColor(Brand.WarmBackground)
            .styleModifier { property("overflow", "hidden") }
            .attrsModifier { attr("id", TENANT_MAP_ID) },
    )
}

private suspend fun initTenantMap(
    lat: Double?,
    lng: Double?,
    address: String?,
    logoUrl: String?,
    accentHex: String,
) {
    val el = document.getElementById(TENANT_MAP_ID) ?: return
    if (jsTypeOf(el.asDynamic()._leaflet_id) != "undefined") return // already built

    var la = lat
    var ln = lng
    if ((la == null || ln == null) && !address.isNullOrBlank()) {
        val point = geocodeOne(address)
        if (point != null) {
            la = point.first
            ln = point.second
        }
    }
    if (la == null || ln == null) return

    val leaflet = window.asDynamic().L
    if (jsTypeOf(leaflet) == "undefined") return

    val map = leaflet.map(TENANT_MAP_ID, js("({zoomControl:true, scrollWheelZoom:false})"))
        .setView(arrayOf(la, ln), 16)
    leaflet.tileLayer(
        CARTO_VOYAGER_TILES,
        js("({maxZoom:20, subdomains:'abcd', attribution:'© OpenStreetMap, © CARTO'})"),
    ).addTo(map)

    val iconOpts = js("({})")
    iconOpts.html = pinHtml(logoUrl, accentHex)
    iconOpts.className = ""
    iconOpts.iconSize = arrayOf(54, 70)
    iconOpts.iconAnchor = arrayOf(27, 70)
    val markerOpts = js("({})")
    markerOpts.icon = leaflet.divIcon(iconOpts)
    markerOpts.interactive = false
    leaflet.marker(arrayOf(la, ln), markerOpts).addTo(map)

    el.asDynamic()._qkmap = map
    window.setTimeout({ map.invalidateSize() }, 200)
    window.setTimeout({ map.invalidateSize() }, 600)
}

/** Logo pin (badge + tail) sitting above a ground dot; anchored at the dot. */
private fun pinHtml(logoUrl: String?, accentHex: String): String {
    val badge =
        if (logoUrl != null) {
            "background:#fff url('$logoUrl') center/cover no-repeat"
        } else {
            "background:$accentHex"
        }
    return """
        <div style="display:flex;flex-direction:column;align-items:center;width:54px;height:70px;">
          <div style="width:48px;height:48px;border-radius:50%;box-sizing:border-box;border:3px solid $accentHex;box-shadow:0 4px 12px rgba(0,0,0,.35);$badge;"></div>
          <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:11px solid $accentHex;margin-top:-1px;filter:drop-shadow(0 2px 1px rgba(0,0,0,.2));"></div>
          <div style="width:7px;height:7px;border-radius:50%;background:rgba(0,0,0,.4);margin-top:3px;"></div>
        </div>
    """.trimIndent()
}

/** Geocode a free-text address via Nominatim; returns the first result's coordinates. */
private suspend fun geocodeOne(query: String): Pair<Double, Double>? {
    val url = "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
        (js("encodeURIComponent")(query) as String)
    val results = runCatching {
        val response = window.fetch(url).await()
        response.json().await().asDynamic()
    }.getOrNull() ?: return null
    val count = (results.length as? Int) ?: 0
    if (count == 0) return null
    return (results[0].lat as String).toDouble() to (results[0].lon as String).toDouble()
}
